import React, { type FunctionComponent, useState } from 'react'
import faker from 'faker'
import { Button, NotificationLevel, notify, Text } from '@retailer-platform/shared-components'
import { Divider } from '@retailer-platform/shared-components/src/tds'
import { ConfirmIcon } from '@instacart/ids-core'
import {
  Property,
  PropertyTitle,
} from '../../components/md-platform-list/components/MdPlatformListTableRow.tsx'
import {
  Input,
  TextInput,
} from '../../components/md-platform-list/components/MdPlatformModifyModal.tsx'
import {
  type AttributeManagementStep,
  type AttributeManagementCategoryEnum,
  type AttributeManagementTypeEnum,
  AttributeManagementCategoryEnumToGraphQLEnum,
  DefaultStep,
  TypesForCategory,
} from '../../types/attributeManagement.types.ts'
import {
  ButtonRow,
  Container,
  PropertyContainer,
  PropertySectionLeft,
  PropertySectionRight,
  SectionHeader,
} from '../shared-styled-components/SharedStyledComponents.tsx'
import { useDomainGoToPath } from '../../utils/domain/routing.ts'
import { useDomainMessages } from '../../utils/domain/intl.ts'
import { useCreateNewStepMutation } from '../../api/index.ts'
import StepAttributeForm, {
  validateAttributes,
} from '../attribute-management-common/step-attribute-forms/StepAttributeForm.tsx'

const validateStep = (
  step: AttributeManagementStep | null,
  category: AttributeManagementCategoryEnum,
  type: AttributeManagementTypeEnum | undefined
) => {
  if (!step || !step?.name || !step?.owner || !step?.description || !type) {
    return false
  }
  return validateAttributes(category, type, step.versions[0].attributes)
}

interface Props {
  category: AttributeManagementCategoryEnum
}

export const AttributeManagementCreate: FunctionComponent<Props> = ({ category }) => {
  // In the future, we'll need to switch this type around, but for now there's only one type per category.
  const [type, setType] = useState<AttributeManagementTypeEnum | null>(
    TypesForCategory[category as AttributeManagementCategoryEnum].length === 1
      ? TypesForCategory[category as AttributeManagementCategoryEnum][0]
      : null
  )
  const [step, setStep] = useState<AttributeManagementStep | null>(type ? DefaultStep[type] : null)
  const i18n = useDomainMessages({
    labelName: 'catalogAdminDomain.attributeManagement.create.label.name',
    labelDesc: 'catalogAdminDomain.attributeManagement.create.label.description',
    labelModel: 'catalogAdminDomain.attributeManagement.create.label.model',
    labelOwner: 'catalogAdminDomain.attributeManagement.create.label.owner',
    labelOwnerSubtitle: 'catalogAdminDomain.attributeManagement.create.label.owner.subtitle',
    labelPrompt: 'catalogAdminDomain.attributeManagement.create.label.prompt',
    labelProperties: 'catalogAdminDomain.attributeManagement.create.label.properties',
    labelPropertiesSubtile: 'catalogAdminDomain.attributeManagement.create.label.property.subtitle',
    cancelButtonText: 'catalogAdminDomain.attributeManagement.create.button.cancel',
    createButtonText: 'catalogAdminDomain.attributeManagement.create.button.create',
    topSectionHeader: 'catalogAdminDomain.attributeManagement.create.topSectionHeader',
    bottomSectionHeader: 'catalogAdminDomain.attributeManagement.create.bottomSectionHeader',
    errorMessage: 'catalogAdminDomain.attributeManagement.create.error.message',
    successMessage: 'catalogAdminDomain.attributeManagement.create.success.message',
  })

  const goToHomePage = useDomainGoToPath(
    'app-admin-catalog-admin-attribute-management',
    undefined,
    undefined,
    { tab: category?.toLowerCase() ?? '' }
  )

  const [triggerCreation, { data, loading, called }] = useCreateNewStepMutation({
    variables: {
      clientId: 'catalog_camp_ipp',
      idempotencyKey: faker.random.uuid(),
      step: {
        description: step?.description,
        name: step?.name,
        owner: step?.owner,
        stepCategory: AttributeManagementCategoryEnumToGraphQLEnum[category],
        tombstoned: false,
        type: type || '',
      },
      attributes: step?.versions[0].attributes,
    },
    onError: () => {
      notify({
        level: NotificationLevel.Error,
        contents: i18n.errorMessage,
      })
    },
  })

  if (data?.instacart_catalog_catflow_v1_CatflowAPI_CreateNewStep?.id) {
    const categoryDisplayName = category.toLowerCase().replace('_', ' ')
    notify({
      icon: <ConfirmIcon />,
      contents: `${
        categoryDisplayName.charAt(0).toUpperCase() + categoryDisplayName.slice(1)
      } step created successfully`,
    })
    goToHomePage()
  }

  const valid = validateStep(step, category, step?.type)

  if (!type) {
    return (
      <div>
        <Text size="small" weight="semibold">
          Select a type of {category} step to create:
        </Text>
        {TypesForCategory[category as AttributeManagementCategoryEnum].map(type => (
          <Button
            key={type}
            onClick={() => {
              setType(type)
              setStep(DefaultStep[type])
            }}
          >
            {type}
          </Button>
        ))}
      </div>
    )
  }

  return (
    <Container>
      <SectionHeader> {i18n.topSectionHeader} </SectionHeader>
      <PropertyContainer>
        <PropertySectionLeft>
          <Property>
            <PropertyTitle>{i18n.labelName} </PropertyTitle>
            <Input
              error={!step?.name}
              value={step?.name}
              onChange={event => {
                if (step) {
                  setStep({ ...step, name: event.target.value })
                }
              }}
            />
          </Property>
          <Property>
            <PropertyTitle>{i18n.labelOwner} </PropertyTitle>
            <Text size="small">{i18n.labelOwnerSubtitle}</Text>
            <Input
              error={!step?.owner}
              value={step?.owner}
              onChange={event => {
                if (step) {
                  setStep({ ...step, owner: event.target.value })
                }
              }}
            />
          </Property>
        </PropertySectionLeft>
        <PropertySectionRight>
          <Property style={{ height: '100%' }}>
            <PropertyTitle>{i18n.labelDesc}</PropertyTitle>
            <TextInput
              value={step?.description}
              error={!step?.description}
              onChange={event => {
                if (step) {
                  setStep({ ...step, description: event.target.value })
                }
              }}
            />
          </Property>
        </PropertySectionRight>
      </PropertyContainer>

      <Divider />
      <StepAttributeForm
        type={type}
        attributes={step?.versions[0].attributes}
        onChange={attributes => {
          if (step) {
            setStep({ ...step, versions: [{ ...step.versions[0], attributes }] })
          }
        }}
        mode={'edit'}
        version={1}
      />
      <ButtonRow>
        <Button
          variant={'secondary'}
          disabled={loading && called}
          onClick={() => goToHomePage()}
          title={i18n.cancelButtonText}
        >
          {i18n.cancelButtonText}
        </Button>
        <Button
          variant={'primary'}
          disabled={!valid || (loading && called)}
          onClick={() => triggerCreation()}
          title={i18n.createButtonText}
        >
          {i18n.createButtonText}
        </Button>
      </ButtonRow>
    </Container>
  )
}
